import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FormData } from '../CareersApplicationForm';

interface AIExperienceStepProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

const aiTools = [
  'ChatGPT', 'Claude', 'GitHub Copilot', 'Midjourney', 'DALL-E', 'Stable Diffusion',
  'Notion AI', 'Jasper', 'Copy.ai', 'Grammarly', 'Figma AI', 'Adobe Sensei',
  'TensorFlow', 'PyTorch', 'Hugging Face', 'LangChain', 'OpenAI API', 'Google Bard',
  'Microsoft Copilot', 'Anthropic API'
];

const AIExperienceStep: React.FC<AIExperienceStepProps> = ({ formData, updateFormData }) => {
  const handleToolToggle = (tool: string, checked: boolean) => {
    let updatedTools;
    if (checked) {
      updatedTools = [...formData.aiToolsUsed, tool];
    } else {
      updatedTools = formData.aiToolsUsed.filter(t => t !== tool);
    }
    updateFormData({ aiToolsUsed: updatedTools });
  };

  const characterCount = formData.aiProjectDescription.length;
  const minLength = 50;
  const isValidLength = characterCount >= minLength;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">AI Experience</h3>
        <p className="text-sm text-gray-600 mb-6">
          We're an AI-forward company. Tell us about your experience with AI tools and how you've used them in your work.
        </p>
      </div>

      <div>
        <Label className="text-base font-medium">AI Experience Level *</Label>
        <RadioGroup
          value={formData.aiExperienceLevel}
          onValueChange={(value) => updateFormData({ aiExperienceLevel: value })}
          className="mt-3"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="beginner" id="beginner" />
            <Label htmlFor="beginner" className="font-normal">Beginner - I'm just starting to explore AI tools</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="intermediate" id="intermediate" />
            <Label htmlFor="intermediate" className="font-normal">Intermediate - I use AI tools effectively for common tasks</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="advanced" id="advanced" />
            <Label htmlFor="advanced" className="font-normal">Advanced - I integrate AI tools into complex workflows</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="expert" id="expert" />
            <Label htmlFor="expert" className="font-normal">Expert - I develop with AI APIs and create custom AI solutions</Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label className="text-base font-medium">Which AI tools have you used? * (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 max-h-60 overflow-y-auto">
          {aiTools.map((tool) => (
            <div key={tool} className="flex items-center space-x-2">
              <Checkbox
                id={tool}
                checked={formData.aiToolsUsed.includes(tool)}
                onCheckedChange={(checked) => handleToolToggle(tool, !!checked)}
              />
              <Label htmlFor={tool} className="text-sm font-normal">{tool}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="favoriteAITool">Favorite AI Tool *</Label>
        <Input
          id="favoriteAITool"
          value={formData.favoriteAITool}
          onChange={(e) => updateFormData({ favoriteAITool: e.target.value })}
          placeholder="What's your go-to AI tool and why?"
        />
      </div>

      <div>
        <Label htmlFor="aiProjectDescription">Describe a specific AI project or use case *</Label>
        <Textarea
          id="aiProjectDescription"
          value={formData.aiProjectDescription}
          onChange={(e) => updateFormData({ aiProjectDescription: e.target.value })}
          placeholder="Tell us about a specific project or task where you used AI tools. What did you build or accomplish? What tools did you use and what was the outcome?"
          rows={4}
        />
        <div className={`text-sm mt-1 ${isValidLength ? 'text-green-600' : 'text-red-500'}`}>
          {characterCount}/{minLength} characters minimum required {isValidLength ? '✓' : '(need ' + (minLength - characterCount) + ' more)'}
        </div>
      </div>
    </div>
  );
};

export default AIExperienceStep;